import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

/**
 * One committed set of advanced-search criteria.
 *
 * `facilityType` (e.g. "ASC", "Hospital") is its own field on both
 * facilities and professionals, distinct from `tier` (a network tier) —
 * the "Facility Type" dropdown used to read `tier` for lack of a real
 * facility-type field on the payload; that field now exists.
 * `physicianQuery`/`facilityQuery` are `""`, not `null`: they back a
 * controlled text input, which needs a string to control.
 */
export type HcbbAdvancedFilters = {
  networkName: string | null;
  facilityType: string | null;
  specialty: string | null;
  physicianQuery: string;
  facilityQuery: string;
};

export type HcbbAdvancedFiltersState = {
  /** The search these filters describe — see `toHcbbResultKey`. */
  resultKey: string | null;
  isPanelOpen: boolean;
  filters: HcbbAdvancedFilters;
};

export const INITIAL_HCBB_ADVANCED_FILTER_VALUES: HcbbAdvancedFilters = {
  networkName: null,
  facilityType: null,
  specialty: null,
  physicianQuery: "",
  facilityQuery: "",
};

export const INITIAL_HCBB_ADVANCED_FILTERS: HcbbAdvancedFiltersState = {
  resultKey: null,
  isPanelOpen: false,
  filters: INITIAL_HCBB_ADVANCED_FILTER_VALUES,
};

/**
 * Rebases the filters on a new search before applying a change.
 *
 * A stale search's open panel, or its criteria, should never carry over onto
 * a brand-new result set — every write carries the key it belongs to, so
 * this is recognised on the first write against a new key rather than
 * through a reset step elsewhere that a later change could forget to fire.
 */
const forKey = (state: HcbbAdvancedFiltersState, resultKey: string) => {
  if (state.resultKey === resultKey) return;

  state.resultKey = resultKey;
  state.isPanelOpen = INITIAL_HCBB_ADVANCED_FILTERS.isPanelOpen;
  state.filters = INITIAL_HCBB_ADVANCED_FILTER_VALUES;
};

const hcbbAdvancedFiltersSlice = createSlice({
  name: "hcbbAdvancedFilters",
  initialState: INITIAL_HCBB_ADVANCED_FILTERS,
  reducers: {
    setHcbbAdvancedFiltersOpen: (
      state,
      action: PayloadAction<{ resultKey: string; isOpen: boolean }>,
    ) => {
      forKey(state, action.payload.resultKey);
      state.isPanelOpen = action.payload.isOpen;
    },
    setHcbbAdvancedFilters: (
      state,
      action: PayloadAction<{
        resultKey: string;
        filters: HcbbAdvancedFilters;
      }>,
    ) => {
      forKey(state, action.payload.resultKey);
      state.filters = action.payload.filters;
    },
    /** Resets criteria only — Clear All is something to keep editing from, not a close action. */
    clearHcbbAdvancedFilters: (
      state,
      action: PayloadAction<{ resultKey: string }>,
    ) => {
      forKey(state, action.payload.resultKey);
      state.filters = INITIAL_HCBB_ADVANCED_FILTER_VALUES;
    },
  },
});

export const {
  setHcbbAdvancedFiltersOpen,
  setHcbbAdvancedFilters,
  clearHcbbAdvancedFilters,
} = hcbbAdvancedFiltersSlice.actions;

/**
 * Named, unlike the older slices' default export: the standards ratchet
 * counts default exports outside route pages, and a name is what the store
 * imports anyway.
 */
export const hcbbAdvancedFiltersReducer = hcbbAdvancedFiltersSlice.reducer;
